import React, { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { t } from 'ttag';
import config from '../../../../config/channels';
import useBasketContext from '../../../../contexts/basket/useBasketContext';
import { TChannel } from '../../../../contexts/operator/operator-context';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import useSiteContext from '../../../../contexts/site/useSiteContext';
import LeavingModal from "../LeavingModal";
import useGroupContext from "../../../../contexts/group/useGroupContext";
import { LeaveGroupModal } from "./group/GroupWarningModals";
import {iLog} from "../../../../index";

interface MenuNavProps {
    channel: TChannel;
    singleSiteAndChannel: boolean;
    // there for catering sites that allow group orders, until one is started
    onStartGroup?: () => void;
}

const MenuNav = ({ channel, singleSiteAndChannel = false, onStartGroup }: MenuNavProps) => {
    const navigate = useNavigate();

    const siteContext = useSiteContext();
    const operatorContext = useOperatorContext();
    const basketContext = useBasketContext();
    const themeContext = useContext(ThemeContext);
    const groupContext = useGroupContext();

    const isDineIn = siteContext.channel === config.dineIn.name;

    const [leavingShow, setLeavingShow] = useState(false);
    const [leaveGroupShow, setLeaveGroupShow] = useState(false);

    const address = JSON.parse(localStorage.getItem('address') ?? '{}');

    const handleLeavingClick = () => {
        // a guest has no location of their own to change: it's the host's order
        if ( groupContext.active && !groupContext.isLead ) {
            setLeaveGroupShow(true);
        } else if ( basketContext.basketItems.length > 0 ) {
            setLeavingShow(true);
        } else {
            window.scrollTo(0,0);
            navigate(`/${isDineIn ? config.dineIn.handle : siteContext.channel}`);
        }
    };

    return (
        <StyledNav className="menu-nav-wrapper">
            <NavContent>
                <StyledNavBrand onClick={handleLeavingClick} className="logoClass" />
                <StyledRightPanel>
                    <div className={'overflow-location'}>
                        <StyledLocation
                            role="button"
                            onClick={handleLeavingClick}
                        >
                            <span className="material-symbols-outlined">location_on</span>

                            {siteContext.channel === 'collection' && (
                                <>
                                    <span className="location">Collection &middot; {siteContext.site?.name}</span>
                                </>
                            )}

                            { ['delivery', 'catering'].includes(siteContext.channel) && <>
                                <span className="location">Delivery &middot; { localStorage.getItem('postcode') ?? siteContext.site?.name ?? '/' }</span>
                            </>}

                            {siteContext.channel === 'venues' && <>
                                <span className="location">Delivery &middot; { address.deliveryVenueName ?? localStorage.getItem('postcode') ?? '/' }</span>
                            </>}

                            {siteContext.channel === 'dine_in' && <>
                                <span className="location">{t`Table `} {basketContext.tableNumber} &middot; {siteContext.site?.name}</span>
                            </>}
                        </StyledLocation>
                    </div>
                    { onStartGroup && (
                        <StyledGroupLink type="button" className="start-group-order" onClick={onStartGroup}>
                            <span className="material-symbols-outlined">group_add</span>
                            <span>{t`Start group order`}</span>
                        </StyledGroupLink>
                    )}
                </StyledRightPanel>
                <LeavingModal
                    show={leavingShow}
                    onHide={() => { setLeavingShow(false); }}
                />
                <LeaveGroupModal
                    show={leaveGroupShow}
                    onHide={() => setLeaveGroupShow(false)}
                    onConfirm={async () => {
                        await groupContext.leave();
                        setLeaveGroupShow(false);
                        basketContext.clearBasket();
                        navigate('/');
                    }}
                />
            </NavContent>
        </StyledNav>
    );
};

const StyledLocation = styled.div`
    display: flex;
    align-items: center;
    height: 36px;
    padding-top: 16px;
    overflow: hidden;

    @media screen and (min-width: 768px) {
        padding: 0 0 0 16px;
    }

    span {
        display: inline-block;
        color: var(--color_ui_4);
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .material-symbols-outlined {
        margin-right: 4px;
        font-size: 24px;
        line-height: 24px;
    }

    .location {
        text-decoration: underline;
        text-underline-offset: 2px;
    }
`;

const StyledGroupLink = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    height: 36px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--color_ui_4);
    font: inherit;
    cursor: pointer;

    &:focus { outline: none; }

    .material-symbols-outlined {
        font-size: 24px;
        line-height: 24px;
    }

    span:last-child {
        text-decoration: underline;
        text-underline-offset: 2px;
        white-space: nowrap;
    }

    @media screen and (max-width: 767px) {
        padding-top: 16px;
    }
`;

const StyledRightPanel = styled.div`
    display: flex;
    gap: 24px;
    min-width: 0;
    align-items: center;
    flex-shrink: 1;
    .overflow-location {
        overflow: hidden;
    }
`;

const StyledNav = styled.div`
    position: relative;
    z-index: 100;
    padding: 8px 0;
`;

const NavContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
`;

const StyledNavBrand = styled.div`
    background-image: var(--logo);
    background-size: contain;
    background-position: center left;
    background-repeat: no-repeat;
    height: var(--logo_height);
    width: 100%;
    display: block;
    cursor: pointer;
    flex-shrink: 0;

    @media screen and (min-width: 768px) {
        width: 290px;
    }
`;

export default MenuNav;
